<template>
    <div class="page-container">
        <div class="settings-form">
            <h2 class="page-title">模版管理</h2>

            <!-- 搜索区域 -->
            <div class="search-bar">
                <el-input v-model="searchForm.moban_name" placeholder="请输入模版名称" clearable size="large"
                    style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>

                <el-button type="primary" size="large" @click="handleSearch">
                    <el-icon>
                        <Search />
                    </el-icon>
                    搜索
                </el-button>

                <el-button type="success" size="large" @click="openAddDialog" style="float: right">
                    <el-icon>
                        <Plus />
                    </el-icon>
                    新增模版
                </el-button>
            </div>

            <!-- 分类标签 -->
            <div class="tabs-wrapper">
                <el-tabs v-model="activeTab" @tab-change="changeTab">
                    <el-tab-pane label="全部模版" name="all"></el-tab-pane>
                    <el-tab-pane label="私有模版" name="private"></el-tab-pane>
                    <el-tab-pane label="共享模版" name="public"></el-tab-pane>
                </el-tabs>
            </div>

            <!-- 模版列表 -->
            <el-table :data="mobanList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
                <el-table-column prop="moban_name" label="模版名称" width="180" />
                <el-table-column label="模版封面" width="200">
                    <template #default="{ row }">
                        <div class="image-preview">
                            <el-image :src="row.moban_img_url" fit="cover"
                                style="width: 60px; height: 60px; margin-right: 8px; border-radius: 4px;"
                                @click="openCustomPreview(row)" hide-on-click-modal>
                                <div v-if="row.moban_share === 1" class="image-badge public-badge">共享</div>
                                <div v-else class="image-badge private-badge">私有</div>
                            </el-image>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column prop="moban_yw_url" label="原模版地址" min-width="180" show-overflow-tooltip>
                    <template #default="{ row }">
                        <template v-if="row.moban_yw_url === '无链接'">
                            <span class="no-link-text">无链接</span>
                        </template>
                        <template v-else>
                            <el-link :href="row.moban_yw_url" target="_blank" type="primary">{{ row.moban_yw_url
                            }}</el-link>
                        </template>
                    </template>
                </el-table-column>
                <el-table-column label="共享状态" width="100">
                    <template #default="{ row }">
                        <el-tag v-if="row.moban_share === 1" type="success">共享模版</el-tag>
                        <el-tag v-else type="warning">私有模版</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="moban_status" label="状态" width="100">
                    <template #default="{ row }">
                        <el-tag v-if="row.moban_status === 1" type="success">启用</el-tag>
                        <el-tag v-else type="info">禁用</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="notes" label="备注" width="150" />
                <el-table-column prop="create_time" label="创建时间" width="180" />
                <el-table-column prop="update_time" label="更新时间" width="180" />

                <el-table-column label="操作" width="180" fixed="right">
                    <template #default="scope">
                        <el-button type="primary" size="small" @click="openEditDialog(scope.row)"
                            :disabled="!isOwner(scope.row)">
                            <el-icon>
                                <Edit />
                            </el-icon>
                            编辑
                        </el-button>
                        <el-button type="danger" size="small" @click="handleDelete(scope.row.id)"
                            :disabled="!isOwner(scope.row)">
                            <el-icon>
                                <Delete />
                            </el-icon>
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>

            <!-- 分页组件 -->
            <div class="pagination-container">
                <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                    :total="pagination.total" :page-sizes="[10, 20, 30, 50]"
                    layout="total, sizes, prev, pager, next, jumper" @size-change="fetchMobanList"
                    @current-change="fetchMobanList" />
            </div>
        </div>

        <!-- 自定义图片预览容器 -->
        <div v-if="previewVisible" class="custom-preview-container" @click="closePreview">
            <div class="preview-content">
                <img :src="currentPreviewImage" alt="预览图" />
                <div class="image-info">
                    <div class="info-item">{{ previewTitle }}</div>
                    <div class="info-item">尺寸: {{ imageSize }}</div>
                </div>
            </div>
            <div class="close-button">
                <el-icon @click.stop="closePreview">
                    <Close />
                </el-icon>
            </div>
        </div>

        <!-- 新增/编辑对话框 -->
        <el-dialog v-model="dialogVisible" :title="dialogTitle" width="700px" :close-on-click-modal="false">
            <el-form ref="mobanForm" :model="mobanForm" label-width="120px" :rules="formRules">
                <el-form-item label="模版名称" prop="moban_name">
                    <el-input v-model="mobanForm.moban_name" placeholder="请输入模版名称" size="large" />
                </el-form-item>

                <el-form-item label="模版地址" prop="moban_yw_url">
                    <div class="link-container">
                        <el-input v-model="mobanForm.moban_yw_url" placeholder="请输入原模版地址" size="large"
                            :disabled="mobanForm.noLink" />
                        <div class="no-link-checkbox">
                            <el-checkbox v-model="mobanForm.noLink" @change="handleNoLinkChange">
                                无链接
                            </el-checkbox>
                        </div>
                    </div>

                </el-form-item>

                <el-form-item label="模版封面" prop="moban_img_url">
                    <div class="image-upload-container">
                        <!-- 单张图片展示区域 -->
                        <div v-if="mobanForm.moban_img_url" class="image-item">
                            <el-image :src="mobanForm.moban_img_url" fit="cover" class="uploaded-image"
                                :preview-src-list="[mobanForm.moban_img_url]" hide-on-click-modal />
                            <div class="image-actions">
                                <el-button type="danger" size="small" circle @click="removeImage">
                                    <el-icon>
                                        <Delete />
                                    </el-icon>
                                </el-button>
                            </div>
                        </div>
                        <!-- 图片上传组件 -->
                        <el-upload action="#" :auto-upload="false" :show-file-list="false"
                            :on-change="handleImageChange" accept="image/*" :disabled="!!mobanForm.moban_img_url">
                            <el-button type="primary" size="large" :disabled="!!mobanForm.moban_img_url">
                                <el-icon>
                                    <Plus />
                                </el-icon>
                                上传图片
                            </el-button>
                        </el-upload>
                    </div>
                </el-form-item>

                <el-form-item label="模版状态" prop="moban_status">
                    <el-switch v-model="mobanForm.moban_status" :active-value="1" :inactive-value="2" active-text="启用"
                        inactive-text="禁用" />
                </el-form-item>

                <el-form-item label="模版内容" prop="moban_content">
                    <RichTextEditor ref="richTextEditor" v-model:content="mobanForm.moban_content"
                        @error="handleEditorError" @show-html="showHtmlDialog" />

                            <!-- 使用 Element Plus 的提示样式 -->
    <el-alert title="请手动复制推文内容到上方内容框，请自行排版，去掉无法显示的图片，做好换行等！" 
        type="error" 
        :closable="false"
        style="margin-top: 8px;" />
                </el-form-item>

                <el-form-item label="备注信息">
                    <el-input v-model="mobanForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitMobanForm">确认</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { ElMessage } from 'element-plus';

import {
    Search, Plus, Edit, Delete, Close
} from '@element-plus/icons-vue';
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils';
import RichTextEditor from '@/components/RichTextEditor.vue';

export default {
    name: 'SystemMoban',
    components: { Search, Plus, Close, Edit, Delete, RichTextEditor },
    data() {
        return {
            loading: false,
            dialogVisible: false,
            dialogTitle: '新增模版',
            isEditMode: false,
            activeTab: 'all', // 当前激活的标签页
            previewVisible: false,
            currentPreviewImage: '',
            previewTitle: '',
            imageSize: '',
            searchForm: {
                moban_name: ''
            },
            mobanForm: {
                id: null,
                store_id: null,
                moban_yw_url: '',
                noLink: false, // 新增：无链接状态

                moban_name: '',
                moban_img_url: '',
                moban_content: '',
                moban_status: 1,
                moban_share: 2, // 默认为私有
                notes: '',
            },
            formRules: {
                moban_name: [
                    { required: true, message: '请输入模版名称', trigger: 'blur' }
                ],
                moban_yw_url: [
                    { required: true, message: '请输入原文地址', trigger: 'blur' }
                ],
                moban_content: [
                    { required: true, message: '请输入模版内容', trigger: 'blur' }
                ]
            },
            mobanList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            }
        }
    },
    computed: {
        // 从Cookie获取商户信息
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}');
        }
    },
    mounted() {
        this.fetchMobanList();
    },
    methods: {
        // 打开自定义预览
        openCustomPreview(row) {
            this.currentPreviewImage = row.moban_img_url;
            this.previewTitle = row.moban_name;
            this.calculateImageSize(row.moban_img_url);
            this.previewVisible = true;
            document.body.classList.add('no-scroll'); // 禁止背景滚动
        },


        // 尝试从响应数据设置封面
        setCoverFromResponse(responseData) {
            // 尝试从HTML内容中提取第一张图片
            const imgRegex = /<img[^>]+src="([^">]+)"/;
            const match = responseData.content_html.match(imgRegex);

            if (match && match[1]) {
                this.mobanForm.moban_img_url = match[1];
                ElMessage.info('已提取文章第一张图片作为封面');
                return;
            }

            // 尝试从自定义字段获取图片（如果有）
            if (responseData.thumbnail_url) {
                this.mobanForm.moban_img_url = responseData.thumbnail_url;
                ElMessage.info('已设置文章缩略图作为封面');
            } else if (responseData.cover_img) {
                this.mobanForm.moban_img_url = responseData.cover_img;
                ElMessage.info('已设置文章封面图');
            }
        },
        // 关闭预览
        closePreview() {
            this.previewVisible = false;
            document.body.classList.remove('no-scroll');
        },

        // 计算图片尺寸
        calculateImageSize(url) {
            const img = new Image();
            img.onload = () => {
                this.imageSize = `${img.width}×${img.height}像素`;
            };
            img.src = url;
        },
        // 显示HTML代码弹窗
        showHtmlDialog(html) {
            this.$prompt('HTML代码', '查看HTML源码', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                inputType: 'textarea',
                inputValue: html,
                customClass: 'html-code-dialog',
                inputAttributes: {
                    rows: 10,
                    style: 'width: 100%; font-family: monospace;'
                }
            }).then(() => { }).catch(() => { });
        },

        // 处理富文本编辑器错误
        handleEditorError(message) {
            this.$message.error(message);
        },
        // 获取模版列表
        async fetchMobanList() {
            this.loading = true;
            try {
                const params = {
                    store_id: this.userInfo.id,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize,
                    tab: this.activeTab // 添加tab参数
                };

                if (this.searchForm.moban_name) {
                    params.moban_name = this.searchForm.moban_name;
                    params.action = 'search';
                } else {
                    params.action = 'list';
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/moban_manager.php`,
                    params
                );

                if (data.data) {
                    this.mobanList = data.data.map(item => {
                        return {
                            ...item,
                            store_id: item.store_id || this.userInfo.id // 确保store_id存在
                        };
                    });
                    this.pagination.total = data.pagination?.total || data.data.length;
                }
            } catch (error) {
                this.$message.error('获取模版列表失败');
                console.error('获取模版列表错误:', error);
            } finally {
                this.loading = false;
            }
        },

        // 处理搜索
        handleSearch() {
            this.pagination.page = 1;
            this.fetchMobanList();
        },

        // 切换标签页
        changeTab() {
            this.pagination.page = 1;
            this.fetchMobanList();
        },

        // 处理无链接复选框变化
        handleNoLinkChange(val) {
            if (val) {
                // 勾选"无链接"时
                this.mobanForm.moban_yw_url = '无链接';
            } else {
                // 取消勾选时清空链接
                this.mobanForm.moban_yw_url = '';
            }
        },

        // 打开新增对话框
        openAddDialog() {
            this.dialogTitle = '新增模版';
            this.isEditMode = false;
            this.mobanForm = {
                id: null,
                store_id: this.userInfo.id,
                moban_yw_url: '',
                noLink: false, // 新增
                moban_name: '',
                moban_img_url: '',
                moban_content: '',
                moban_status: 1,
                moban_share: 2,
                notes: '',
            };
            // 添加关键代码：延迟确保编辑器内容重置
            setTimeout(() => {
                // 显式触发内容更新
                if (this.$refs.richTextEditor) {
                    this.$refs.richTextEditor.clearContent(); // 调用富文本编辑器组件的清空方法
                }
            }, 100);


            this.dialogVisible = true;
        },

        // 打开编辑对话框
        openEditDialog(row) {
            if (!this.isOwner(row)) {
                this.$message.warning('您没有权限编辑此模版');
                return;
            }

            this.dialogTitle = '编辑模版';
            this.isEditMode = true;
            this.mobanForm = {
                id: row.id,
                store_id: row.store_id,
                moban_yw_url: row.moban_yw_url,
                noLink: row.moban_yw_url === '无链接', // 设置为布尔值
                moban_name: row.moban_name,
                moban_img_url: row.moban_img_url,
                moban_content: row.moban_content,
                moban_status: row.moban_status,
                moban_share: row.moban_share,
                notes: row.notes,
            };
            // 添加关键代码
            setTimeout(() => {
                if (this.$refs.richTextEditor) {
                    this.$refs.richTextEditor.setContent(row.moban_content);
                }
            }, 100);
            this.dialogVisible = true;
        },

        // 检查当前用户是否是模版的所有者
        isOwner(row) {
            // 当前用户是创建者
            if (row.store_id === this.userInfo.id) return true;

            // 共享模版允许所有人查看，但不能修改
            if (row.moban_share === 1) return false;

            return false;
        },

        // 处理图片上传
        async handleImageChange(file) {
            try {
                this.$message.info('图片上传中，请稍候...');

                // 显示上传进度
                const loading = this.$loading({
                    lock: true,
                    text: '图片上传中...',
                    spinner: 'el-icon-loading',
                    background: 'rgba(0, 0, 0, 0.7)'
                });

                // 获取上传签名
                const signature = await getUploadSignature(file.raw.type.split('/')[0]);

                // 上传到OSS
                const res = await uploadToOSS(file.raw, signature);

                // 设置图片URL
                this.mobanForm.moban_img_url = res.url;

                this.$message.success('图片上传成功');
                loading.close();

            } catch (error) {
                console.error('图片上传失败:', error);
                this.$message.error('图片上传失败: ' + (error.message || '服务器错误'));
                this.$loading().close();
            }
        },

        // 删除图片
        removeImage() {
            this.mobanForm.moban_img_url = '';
        },

        // 提交表单（新增/编辑）
        submitMobanForm() {
            this.$refs.mobanForm.validate(async (valid) => {
                if (valid) {
                    try {
                        // 检查模版封面
                        if (!this.mobanForm.moban_img_url) {
                            this.$message.warning('请上传模版封面图片');
                            return;
                        }

                        const action = this.isEditMode ? 'update' : 'add';

                        const params = {
                            ...this.mobanForm,
                            store_id: this.userInfo.id
                        };

                        const { data } = await axios.post(
                            `${config.BASE_URL}/shop/api/moban_manager.php`,
                            {
                                action,
                                ...params
                            }
                        );

                        if (data.success) {
                            this.$message.success(this.isEditMode ? '模版更新成功' : '模版添加成功');
                            this.dialogVisible = false;
                            this.fetchMobanList();
                        } else {
                            this.$message.error(data.error || '操作失败');
                        }
                    } catch (error) {
                        const errorMsg = error.response?.data?.error || error.message || '操作失败';
                        this.$message.error(`操作失败: ${errorMsg}`);
                        console.error('表单提交错误:', error);
                    }
                }
            });
        },

        // 删除模版
        async handleDelete(id) {
            const moban = this.mobanList.find(item => item.id === id);
            if (!moban) return;

            if (!this.isOwner(moban)) {
                this.$message.warning('您没有权限删除此模版');
                return;
            }

            this.$confirm('确定要删除该模版吗？', '警告', {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const { data } = await axios.post(
                        `${config.BASE_URL}/shop/api/moban_manager.php`,
                        {
                            action: 'delete',
                            id: id,
                            store_id: this.userInfo.id
                        }
                    );

                    if (data.success) {
                        this.$message.success('删除成功');
                        this.fetchMobanList();
                    } else {
                        this.$message.error(data.error || '删除失败');
                    }
                } catch (error) {
                    const errorMsg = error.response?.data?.error || error.message || '删除失败';
                    this.$message.error(`删除失败: ${errorMsg}`);
                    console.error('删除错误:', error);
                }
            }).catch(() => { });
        }
    }
}
</script>

<style scoped lang="scss">
.page-container {
    padding: 0px;
}

.settings-form {
    max-width: 95%;
    margin: 0 auto;
    padding: 32px 40px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.link-container {
    display: flex;
    align-items: center;
    gap: 15px;
    width: 100%; // 确保宽度与上方输入框一致

    .el-input {
        flex: 1;
    }

    .no-link-checkbox {
        flex-shrink: 0;
        white-space: nowrap;
    }
}

.parse-template-btn {
    margin-top: 12px;
    text-align: right;

    .el-button {
        width: 140px; // 稍微加宽一点容纳图标
        padding: 8px 15px;
        display: flex;
        align-items: center;
        justify-content: center;

        .el-icon {
            margin-right: 5px;
            font-size: 18px;
        }

        .btn-text {
            vertical-align: middle;
        }
    }
}

@media (max-width: 768px) {
    .link-container {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;

        .el-input {
            width: 100%;
        }
    }

    .parse-template-btn {
        margin-top: 10px;
        text-align: center;
    }
}

.page-title {
    font-size: 24px;
    color: #1f2937;
    margin-bottom: 32px;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.5px;
}

.search-bar {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

.tabs-wrapper {
    background: #f8fafc;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 20px;
    border: 1px solid #ebeef5;

}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

/* 表格样式优化 */
.el-table {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;

    :deep(.el-table__header) th {
        background-color: #f5f7fa;
        color: #606266;
        font-weight: 600;
    }

    :deep(.el-table__body) tr:hover>td {
        background-color: #f0f7ff !important;
    }
}

.image-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    position: relative;
}

.image-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: bold;
    color: white;
    z-index: 2;
}

.public-badge {
    background-color: #67c23a;
}

.private-badge {
    background-color: #e6a23c;
}

/* 图片上传区域 */
.image-upload-container {
    display: flex;
    align-items: center;
    gap: 15px;

    .image-item {
        position: relative;
        width: 120px;
        height: 120px;
        border: 1px dashed #dcdfe6;
        border-radius: 6px;
        overflow: hidden;

        .uploaded-image {
            width: 100%;
            height: 100%;
            display: block;
        }

        .image-actions {
            position: absolute;
            bottom: 0;
            width: 100%;
            display: flex;
            justify-content: center;
            padding: 4px;
            background: rgba(0, 0, 0, 0.5);
        }
    }

    .el-upload {
        height: 40px;
    }
}

.tips {
    font-size: 12px;
    color: #909399;
    margin-top: 6px;
}

/* 操作按钮样式 */
.el-button {
    display: inline-flex;
    align-items: center;

    .el-icon {
        margin-right: 5px;
    }
}

/* 对话框样式优化 */
:deep(.el-dialog) {
    border-radius: 12px;

    .el-dialog__header {
        padding: 20px 24px;
        border-bottom: 1px solid #ebeef5;
        margin-right: 0;

        .el-dialog__title {
            font-weight: 600;
            color: #1f2937;
        }
    }

    .el-dialog__body {
        padding: 24px;
    }

    .el-dialog__footer {
        padding: 16px 24px;
        border-top: 1px solid #ebeef5;
    }
}

/* 表单验证提示样式 */
:deep(.el-form-item__error) {
    font-size: 12px;
    padding-top: 4px;
}

@media (max-width: 768px) {
    .search-bar {
        flex-direction: column;
        align-items: flex-start;
    }

    .search-bar .el-input {
        margin-right: 0;
        margin-bottom: 12px;
        width: 100% !important;
    }

    .search-bar .el-button {
        margin: 5px 0;
    }
}

/* 自定义预览容器样式 */
.custom-preview-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    cursor: zoom-out;

    .preview-content {
        position: relative;
        max-width: 90%;
        max-height: 90%;

        img {
            max-width: 100%;
            max-height: 80vh;
            object-fit: contain;
            border-radius: 4px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
        }

        .image-info {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background: rgba(0, 0, 0, 0.7);
            color: white;
            padding: 10px 15px;
            border-bottom-left-radius: 4px;
            border-bottom-right-radius: 4px;

            .info-item {
                margin: 5px 0;
                font-size: 14px;
            }
        }
    }

    .close-button {
        position: absolute;
        top: 20px;
        right: 20px;
        color: white;
        font-size: 28px;
        cursor: pointer;
        background: rgba(0, 0, 0, 0.5);
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.3s;

        &:hover {
            background: rgba(0, 0, 0, 0.8);
        }
    }
}

/* 全局添加禁止滚动样式 */
.no-scroll {
    overflow: hidden;
}

/* 修复原始表格中的图片预览样式 */
.image-preview {
    cursor: zoom-in;

    .el-image {
        position: relative;
        z-index: 1;
    }
}
</style>